import React, { useMemo } from "react";
import InventoryKPI from "./InventoryKPI";
import useQueryWithRetries from "../../../../Tools/useQueryWithRetries";
import { GET_INVENTORY_CLOUD_ACCOUNTS } from "../../../WidgetDataApi";
import { useIcon, formatNumber } from "@SeemplicityDev/shared";
import type { EntityItem } from "../../../../Components/EntityLogo/EntityList";
import type { SelectedScope } from "../../../../types/scope";

// Raw row shape returned by generic_widget_data for cloud accounts by provider.
type CloudAccountRow = { provider: string; resources_count: number };

interface InventoryCloudAccountsKPIProps {
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  selectedScope?: SelectedScope | null;
  isForExport?: boolean;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

const InventoryCloudAccountsKPI: React.FC<InventoryCloudAccountsKPIProps> = ({
  filterToExecute,
  scopeToExecute,
  selectedScope,
  isForExport,
  setIsReadyToExport = null,
}) => {
  const { data, error, loading, refetch } = useQueryWithRetries({
    query: GET_INVENTORY_CLOUD_ACCOUNTS,

    variables: {
      widget_definition_key: "inventory_cloud_accounts_by_provider",
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { getProviderIcon } = useIcon(isForExport ? "LIGHT" : undefined);

  const processedData = useMemo<EntityItem[] | null>(() => {
    if (!!data) {
      const rawData = (
        data as { generic_widget_data?: { data?: CloudAccountRow[] } }
      )?.generic_widget_data?.data;

      return (
        rawData?.map((item): EntityItem => {
          const { provider, resources_count } = item;
          // getProviderIcon may return null; EntityItem.logo is typed string |
          // undefined but EntityList/EntityLogo handle a nullish logo identically.
          return {
            displayName: provider,
            logo: getProviderIcon(provider) as string | undefined,
            count: resources_count,
          };
        }) ?? null
      );
    }

    return null;
  }, [data, getProviderIcon]);

  return (
    <InventoryKPI
      data={processedData}
      isForExport={isForExport}
      tooltipCallback={(provider) =>
        `${provider?.displayName}: ${formatNumber(provider?.count)} Cloud Resource${(provider?.count ?? 0) > 1 ? "s" : ""}`
      }
      error={error}
      loading={loading}
      refetch={refetch}
      setIsReadyToExport={setIsReadyToExport}
    />
  );
};

export default InventoryCloudAccountsKPI;
